import { useQuery } from "@apollo/client";
import moment from "moment";
import { ReactNode } from "react";
import {
  GetMeetingTranscriptQueryQuery,
  GetMeetingTranscriptQueryQueryVariables,
  MeetingBotSetting,
  MeetingViewMeetingNodeNewPageFragmentFragment,
  PricingTier,
  RecallBotStatus,
} from "types/graphql-schema";

import getMeetingTranscriptQuery from "@apps/meeting-transcription-dialog/graphql/get-meeting-transcript-query";
import BillingDialogCTA from "@components/billing-dialog-cta/billing-dialog-cta";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";
import { classNames } from "@helpers/css";

import MeetingBot from "../meeting-bot";
import MeetingNotetakerCheckbox from "../notetaker-checkbox";

const MeetingSidebarAIContainer = ({
  meeting,
  children,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  children: ({
    meeting,
    refetch,
    loading,
  }: {
    meeting: NonNullable<GetMeetingTranscriptQueryQuery["meeting"]>;
    refetch: () => void;
    loading: boolean;
  }) => ReactNode;
}) => {
  const orgNotOnUltimatePlan =
    meeting.organization?.effectivePricingTier !== PricingTier.Ultimate;
  const summarizationNotEnabled =
    !meeting.organization?.enableMeetingSummarization;

  const { data, loading, refetch } = useQuery<
    GetMeetingTranscriptQueryQuery,
    GetMeetingTranscriptQueryQueryVariables
  >(getMeetingTranscriptQuery, {
    skip: orgNotOnUltimatePlan || summarizationNotEnabled,
    variables: { meetingId: meeting.id },
  });

  if (!meeting.organization || !meeting.meetingGroup) {
    return null;
  }

  const transcriptMeeting = data?.meeting;
  const meetingHasNotStartedYet =
    meeting?.startDatetime && moment().isBefore(meeting.startDatetime);
  const meetingIsProbablyDone =
    meeting?.endDatetime &&
    moment().isAfter(moment(meeting.endDatetime).add(1, "hours"));
  const meetingNotFound = !transcriptMeeting;
  const notetakerWillJoin =
    meeting.meetingGroup.meetingBotSetting === MeetingBotSetting.AlwaysJoin ||
    (meeting.meetingGroup.meetingBotSetting ===
      MeetingBotSetting.UseParticipantsSetting &&
      meeting.botParticipationDetails.willBotJoin);

  const containerClassName =
    "mt-4 bg-gray-50 py-8 px-4 text-center rounded-lg text-gray-700 text-sm leading-relaxed";

  return orgNotOnUltimatePlan ? (
    <div className={containerClassName}>
      <BillingDialogCTA className="text-blue-link underline">
        <span>Upgrade your plan</span>
      </BillingDialogCTA>{" "}
      to have access to the AI Assist feature.
    </div>
  ) : summarizationNotEnabled ? (
    <div className={containerClassName}>
      Enable AI Meeting Summarization & Assistant in{" "}
      <AppLink
        to={`/settings/organization/${meeting.organization?.id}/settings`}
        className="text-blue-600"
      >
        Organization Settings
      </AppLink>
      .
    </div>
  ) : !data && loading ? (
    <div className={containerClassName}>
      <Loading />
    </div>
  ) : meetingNotFound ? (
    <div className={classNames(containerClassName, "text-center")}>
      Meeting not be found.
    </div>
  ) : transcriptMeeting.transcript || transcriptMeeting.notesSummary ? (
    children({ meeting: transcriptMeeting, refetch, loading })
  ) : transcriptMeeting.botStatus === RecallBotStatus.GeneratingSummary ? (
    <div className={classNames(containerClassName, "text-center")}>
      Notetaker is processing the transcript.
      <br />
      The meeting recap will appear ~1 minute after the meeting concludes.
    </div>
  ) : transcriptMeeting.botStatus === RecallBotStatus.InMeeting ? (
    <div className={classNames(containerClassName, "text-center")}>
      Notetaker is in the meeting, transcribing notes.
      <br />
      The meeting recap will appear ~1 minute after the meeting concludes.
    </div>
  ) : transcriptMeeting.botStatus === RecallBotStatus.Scheduled ? (
    <div className={classNames(containerClassName, "text-center")}>
      Notetaker is scheduled to join the meeting at{" "}
      {moment(transcriptMeeting.startDatetime).format(
        "h:mma [on] dddd, MMMM D, YYYY"
      )}
      .
    </div>
  ) : transcriptMeeting.botStatus === RecallBotStatus.JoiningMeeting ? (
    <div className={classNames(containerClassName, "text-center")}>
      Notetaker is trying to join the meeting.
      <br />
      Admit the notetaker to allow them to transcribe and summarize the meeting.
    </div>
  ) : (
    <div className={classNames(containerClassName, "text-center")}>
      <div>No meeting recap available.</div>
      {transcriptMeeting.isRecurring &&
      transcriptMeeting.meetingGroup?.canUpdate?.permission &&
      ((!meetingHasNotStartedYet && !meetingIsProbablyDone) ||
        !notetakerWillJoin) ? (
        <>
          <div>Add the notetaker to your meetings to get a meeting recap.</div>
          <div className="flex justify-center empty:hidden">
            {!meetingHasNotStartedYet && !meetingIsProbablyDone ? (
              <div className="mt-6 border-t border-gray-200 pt-6 px-4 flex justify-center gap-4">
                <MeetingBot meeting={transcriptMeeting} />
              </div>
            ) : !notetakerWillJoin ? (
              <div className="mt-6 border-t border-gray-200 pt-6 px-4 flex justify-center gap-4">
                <MeetingNotetakerCheckbox
                  meeting={transcriptMeeting}
                  className="flex items-center gap-1"
                  label="Add the notetaker to future meetings"
                />
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default MeetingSidebarAIContainer;
